<template>
  <!-- AWS-console-style favorites strip: only rendered when horizon owns the
       chrome (nowhere to put a fixed row in classic) and the user has opted
       in via a "Use Toolbar" checkbox (site map's Favorites section, or the
       left rail's own Favorites section). gates re-apply on every resolve,
       same as the sidebar's favorites section, so a starred page the user
       has lost access to just stops appearing.

       chips only -- no leading star to hide the bar, no per-chip star to
       unfavorite. both were controls this strip could least afford the width
       for, and neither was the only way to reach its action: the same "Use
       Toolbar" checkbox that turned the bar on turns it off, and a page is
       unstarred from its own header star or from the site map. -->
  <div v-if="horizonNavActive && enabled" class="fi-fb">
    <div v-if="favoriteItems.length" class="fi-fb-row">
      <div
        v-for="fav in favoriteItems"
        :key="fav.key"
        class="fi-fb-chip-wrap"
        :class="{
          'fi-fb-chip-wrap--dragging': dragKey === favKeyOf(fav),
          'fi-fb-chip-wrap--drag-over': dragOverKey === favKeyOf(fav) && dragKey !== favKeyOf(fav),
        }"
        :data-fav-key="favKeyOf(fav)"
        draggable="true"
        title="Drag to reorder, or focus and press Alt+Left / Alt+Right"
        @dragstart="onDragStart(fav, $event)"
        @dragover.prevent="onDragOver(fav)"
        @dragleave="onDragLeave(fav)"
        @drop.prevent="onDrop(fav)"
        @dragend="onDragEnd"
      >
        <router-link
          :to="fav.to"
          class="fi-fb-chip"
          draggable="false"
          @keydown.native="onChipKeydown(fav, $event)"
        >
          <i class="fa fi-fb-chip-icon" :class="fav.icon || 'fa-circle-o'" aria-hidden="true"></i>
          <span class="fi-fb-chip-label">{{ chipLabel(fav) }}</span>
        </router-link>
      </div>
      <!-- the way back to the sidebar, revealed only on hover or keyboard
           focus so the strip stays chrome-free in normal use (the sidebar's
           "Use Toolbar" checkbox vanishes along with the section it sits in,
           which left the site map as the only off-switch -- a discoverability
           hole). the bar never renders under 991px, so hover is a safe gate. -->
      <button type="button" class="fi-fb-return"
              title="Show Favorites in the sidebar instead"
              aria-label="Show Favorites in the sidebar instead"
              @click="returnToSidebar">
        <i class="fa fa-outdent" aria-hidden="true"></i>
      </button>
    </div>
    <span v-else class="fi-fb-hint">
      No favorites yet — star a page from the Site Map or its header to pin it here.
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { buildNavContext, resolveNav, resolveFavorites } from '@/nav/registry'
import { setNavFavoritesBarEnabled } from '@/nav/nav_favorites_bar'
import { siteMapFavoriteDestinations } from '@/utils/site_map_pages'
import { favState, favKey, reorderFavorites, nudgeFavorite, ensureFavoritesLoaded } from '@/nav/nav_favorites'
import horizonNav from '@/nav/horizon_nav_mixin'

export default {
  name: 'fi_favorites_bar',
  mixins: [horizonNav],
  data() {
    return {
      favState,
      // canonical favKey (not resolveFavorites' registry-shaped fav.key) of
      // the chip currently being dragged / currently hovered as a drop
      // target -- null when no drag is in progress
      dragKey: null,
      dragOverKey: null,
    }
  },
  mounted() {
    ensureFavoritesLoaded()
    // the row wraps above the mobile breakpoint, so the bar's own height
    // varies with favorite count and viewport width -- republish it as a CSS var
    // on <html> (a shared ancestor of .fi-hz-root and the rail) any time it
    // changes, so both keep clearing the bar exactly instead of assuming a
    // fixed 40px.
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this.syncTopOffset)
      this._ro.observe(this.$el)
    }
    this.syncTopOffset()
  },
  beforeDestroy() {
    if (this._ro) this._ro.disconnect()
    // falls back to the plain 52px top-bar height (see App.vue/fi_section_nav.vue)
    document.documentElement.style.removeProperty('--fi-hz-top-offset')
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
      enabled: state => state.profile.nav_favorites_bar,
    }),
    nav() {
      return resolveNav(buildNavContext(this.user, this.viewAs))
    },
    // second source for stars on pages the rail has no row for -- see
    // resolveFavorites. built here rather than inside it because only the
    // components know the user
    favFallbacks() {
      return siteMapFavoriteDestinations(this.user, this.viewAs)
    },
    // resolveFavorites' items carry the REGISTRY's own structural key
    // ('section:group:item'), not the canonical favKey stored in
    // favState.keys -- favKeyOf() below bridges the two wherever reordering
    // needs to write back into that list.
    favoriteItems() {
      return resolveFavorites(this.nav, this.favState.keys, favKey, this.favFallbacks)
    },
  },
  methods: {
    syncTopOffset() {
      document.documentElement.style.setProperty('--fi-hz-top-offset', (52 + this.$el.offsetHeight) + 'px')
    },
    favKeyOf(fav) {
      return favKey(fav.to)
    },
    // chips are the one place a destination's name competes for horizontal
    // space with every other favorite, so a registry item may offer a tighter
    // one here ("Bookings & Actuals" -> "Bookings"). the rail and the site map
    // keep the full label.
    returnToSidebar() {
      setNavFavoritesBarEnabled(false)
    },
    chipLabel(fav) {
      return fav.favLabel || fav.shortLabel || fav.label
    },
    onDragStart(fav, event) {
      this.dragKey = this.favKeyOf(fav)
      event.dataTransfer.effectAllowed = 'move'
      // firefox requires data to be set for the drag to initiate at all;
      // the value itself is unused (state lives in dragKey)
      try { event.dataTransfer.setData('text/plain', this.dragKey || '') } catch (_) {}
    },
    onDragOver(fav) {
      if (!this.dragKey) return
      const key = this.favKeyOf(fav)
      if (key !== this.dragKey) this.dragOverKey = key
    },
    onDragLeave(fav) {
      if (this.dragOverKey === this.favKeyOf(fav)) this.dragOverKey = null
    },
    // drop lands the dragged chip immediately before the one it was dropped
    // on, computed from the CURRENT on-screen order so a favorite added or
    // removed elsewhere mid-drag can't desync the result
    onDrop(fav) {
      const dropKey = this.favKeyOf(fav)
      if (this.dragKey && this.dragKey !== dropKey) {
        const order = this.favoriteItems.map(i => this.favKeyOf(i))
        const from = order.indexOf(this.dragKey)
        if (from !== -1) {
          order.splice(from, 1)
          const to = order.indexOf(dropKey)
          order.splice(to === -1 ? order.length : to, 0, this.dragKey)
          reorderFavorites(order)
        }
      }
      this.dragKey = null
      this.dragOverKey = null
    },
    onDragEnd() {
      this.dragKey = null
      this.dragOverKey = null
    },
    // Alt+Arrow keyboard reordering, for anyone who can't (or would rather
    // not) drag: swaps with the neighbor and keeps focus on the moved chip.
    onChipKeydown(fav, event) {
      if (!event.altKey) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      const key = this.favKeyOf(fav)
      nudgeFavorite(key, event.key === 'ArrowLeft' ? -1 : 1)
      this.$nextTick(() => {
        const wrap = this.$el.querySelector('[data-fav-key="' + CSS.escape(key) + '"]')
        const link = wrap && wrap.querySelector('.fi-fb-chip')
        if (link) link.focus()
      })
    },
  },
}
</script>

<style scoped>
/* fixed, full width, riding directly under the 52px top bar. height is
   content-driven (padding, not a fixed height): the row wraps to as many
   lines as it needs here, and collapses to one sideways-scrolling line on
   mobile -- syncTopOffset() below measures the real rendered height and
   republishes --fi-hz-top-offset itself, so .fi-hz-root's padding-top (and
   fi_section_nav.vue's rail top) always clear it exactly, whether that's one
   row or several. */
.fi-fb {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  z-index: 1025;
  display: flex;
  align-items: center;
  /* loosened a notch (was 8/5px 14px): a full row of chips at the old spacing
     ran together into one band. syncTopOffset() measures the rendered height,
     so the extra vertical padding pushes the page down with it rather than
     tucking under the bar.
     the 8px vertical is the top bar's own: .fi-tn-bar is a fixed 52px around
     36px controls, which leaves exactly 8px above and below its content, so
     the two bars stack with the same band of air. keep them in step. */
  gap: 12px;
  padding: 8px 18px;
  /* a light wash of the same accent blue the chips use as a solid fill --
     color-mix toward --fi-surface (not literal white) so this stays a pale
     blue in horizon but a blue-tinted dark navy in midnight, rather than
     going bright/washed-out against an otherwise dark theme */
  background: color-mix(in srgb, var(--fi-chrome-accent) 30%, var(--fi-surface) 70%);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border-bottom: 1px solid var(--fi-glass-line);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: left;
}

.fi-fb-hint {
  font-size: 13px;
  /* the bar's own background is a light wash now (not solid dark), so this
     reads like ordinary chrome text again, same token the chip labels used
     before they became white-on-blue below */
  color: var(--fi-chrome-ink-strong);
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fi-fb-row {
  display: flex;
  align-items: center;
  /* fill the bar so the return control's auto margin lands it on the
     bar's right edge, not just past the last chip */
  flex: 1 1 auto;
  /* both axes: the row wraps, so this is the gap BETWEEN wrapped lines too */
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.fi-fb-chip-wrap {
  position: relative;
  flex: none;
  /* flex, not block: the chip inside is inline-flex, so in a block wrap it
     sits in a LINE box and inherits the strut's descender -- ~2px of dead
     space above the chip (the line box is taller than the chip and aligns it
     on the baseline), which the row then centers as if it were part of the
     chip. that parked every chip ~1px below the bar's middle: 10px of air
     above, 8px below. flex has no line box, so the wrap is exactly the
     chip's 22px and .fi-fb's 8px padding is the real air on both sides --
     the same 8px band the top bar keeps around its 36px controls.
     one row of chips therefore measures 38px, not 40; syncTopOffset()
     republishes the measured height, so the page below follows. */
  display: flex;
  border-radius: var(--fi-radius-sm);
  transition: opacity var(--fi-motion-quick);
}

/* dragged chip stays in place but fades, so its slot in the row still
   reads as "this one is moving" rather than just vanishing */
.fi-fb-chip-wrap--dragging { opacity: 0.4; }

/* drop target cue: a white ring, since --fi-chrome-accent is now the chip's
   own fill and would be invisible as a same-color ring on itself */
.fi-fb-chip-wrap--drag-over .fi-fb-chip {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
}

.fi-fb-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  /* the 1px goes on TOP only, and it is an optical correction, not spacing:
     align-items:center centers each item's LINE BOX, and a line box is
     bottom-heavy -- Avenir's descent (0.38em) is dead space under the
     baseline that most labels ("Reports", "Vehicles") never fill, and the FA
     glyph box has the same slack. centering the boxes therefore parks the
     visible glyphs ~0.6px above the chip's optical middle, which reads as a
     tight top edge. border-box (bootstrap's global reset) keeps the chip at
     22px, so this only moves the content, not the bar's height. measured
     after: 6.3px above the caps vs 6.5px under the baseline (was 5.8/7.0). */
  padding: 1px 8px 0;
  /* squared-off button look (matches the buttons token), not a pill */
  border-radius: var(--fi-radius-sm);
  /* the same blue the left rail uses for its active-item icons/pipe --
     solid fill, not a tint, so the chip reads as a distinct button */
  background: var(--fi-chrome-accent);
  color: #fff !important;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none !important;
  cursor: grab;
  transition: background-color var(--fi-motion-quick);
}
.fi-fb-chip:hover { background: color-mix(in srgb, var(--fi-chrome-accent) 82%, black 18%); }
.fi-fb-chip:focus { outline: none; }
.fi-fb-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
}

.fi-fb-chip-icon { font-size: 11px; opacity: 0.8; }

/* phones and small tablets: the bar stays, but it cannot wrap -- two or three
   wrapped rows of chips would eat a third of a phone viewport, and every page
   below is pushed down by whatever this measures (syncTopOffset). one row
   that scrolls sideways instead. */
@media (max-width: 991px) {
  .fi-fb {
    gap: 8px;
    padding: 6px 10px;
  }

  .fi-fb-row {
    flex: 1 1 auto;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    /* the chips are the only thing in the scroller and each is a link, so the
       bar reads as swipeable without a rule of scrollbar sitting under it */
    scrollbar-width: none;
    /* the chips' focus ring is drawn outside their box; without the inset it
       is clipped by the scroller on the first and last chip */
    padding: 2px;
    margin: -2px;
  }

  .fi-fb-row::-webkit-scrollbar { display: none; }

  /* chips keep their natural width inside the scroller rather than being
     squeezed to fit the viewport */
  .fi-fb-chip-wrap { flex: 0 0 auto; }
}

/* the return-to-sidebar control: present for the keyboard (always
   tabbable), invisible until the bar is hovered or it holds focus */
.fi-fb-return {
  /* PARKED (chaz, 2026-08-26): fully hidden for now so nothing unfamiliar
     shows up mid-demo -- delete this display:none to enable the control.
     the implementation below is complete and verified. */
  display: none;
  align-items: center; justify-content: center;
  margin-left: auto; padding: 2px 7px;
  background: transparent; border: 1px solid transparent;
  border-radius: var(--fi-radius-pill, 999px);
  color: var(--fi-text-muted, #6b7280); font-size: 12px;
  cursor: pointer; opacity: 0; transition: opacity .15s, border-color .12s;
}
.fi-fb:hover .fi-fb-return,
.fi-fb-return:focus,
.fi-fb-return:focus-visible { opacity: 1; }
.fi-fb-return:hover { border-color: var(--fi-line-strong, #d6dde6); }
</style>
